// Central Mason Fire & EMS — HydroCalc v5
// Shared constants: colors, data tables, nav config, shared styles

// Navy theme — lighter navy backgrounds with muted, section-distinct accents.
const DARK = {
  bg: "#0b0d0f",   // page — near-black instrument
  bg1: "#15181c",  // cards / header / nav (surface)
  bg2: "#1c2126",  // panels (surfaceAlt)
  bg3: "#242a30",  // inputs
  bdr: "#24282d",  // hairline border
  bdr2: "#333a41", // emphasized border
  red: "#e8753b",  // Ops accent — ember (the one light source)
  red2: "#f0894f", // Ops hover / brighter ember
  white: "#eaf0f8",
  t0: "#e8ebed",   // primary text (16:1 on bg)
  t1: "#cbd2d9",
  t2: "#9ba3aa",   // secondary
  t3: "#868e95",   // faint (still AA)
  ok: "#5fa890",   // success — muted green (shared w/ EMS)
  warn: "#e0a657", // warning — muted amber
  bad: "#dc4a4a",  // danger / alert signal (distinct from ember)
  info: "#5e94c4", // Command accent — muted steel blue
  gold: "#c9a24b", // Training accent — antique gold
  teal: "#46b3a8",
  ems: "#5fa890",  // EMS accent — muted green
  // ── Redesign tokens (additive; consumed by modernized screens) ──
  bdrH: "rgba(255,255,255,0.08)",                 // hairline border — depth without heavy lines
  surf: "#15181c",                                 // tile surface (== bg1)
  sh1: "0 1px 2px rgba(0,0,0,0.4)",                // resting elevation (rarely used in dark)
  sh2: "0 8px 24px rgba(0,0,0,0.5)",               // raised elevation
  fSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", // UI + prose
  fMono: "'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace",                   // numeric readouts
};

// Light theme — cool off-white backgrounds, accents harmonized with the navy
// dark theme (same section identities, darkened to read on light surfaces).
const LIGHT = {
  bg: "#f4f2ec",    // warm off-white page (daylight readable)
  bg1: "#ffffff",   // cards / header / nav (surface)
  bg2: "#faf8f3",   // panels (surfaceAlt)
  bg3: "#f1eee6",   // inputs
  bdr: "#e2ded3",   // hairline border
  bdr2: "#cfc9bb",  // emphasized border
  red: "#b23a2e",   // Ops accent — deep brick
  red2: "#c24234",
  white: "#ffffff",
  t0: "#1c2026",    // primary text
  t1: "#3a3f47",
  t2: "#54585f",    // secondary
  t3: "#6b6f75",    // faint (AA on paper)
  ok: "#1e7a5a",
  warn: "#a4702a",
  bad: "#b3433b",
  info: "#1f6fa8",  // Command accent — blue
  gold: "#8a5f10",  // Training accent — deep ochre
  teal: "#2c857c",
  ems: "#1e7a5a",   // EMS accent — pine green
  // ── Redesign tokens (additive; consumed by modernized screens) ──
  bdrH: "rgba(28,32,38,0.12)",                     // hairline border
  surf: "#ffffff",                                 // neutral tile surface
  sh1: "0 1px 2px rgba(28,32,38,0.06)",            // resting elevation
  sh2: "0 6px 16px rgba(28,32,38,0.10)",           // raised elevation
  fSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fMono: "'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

export const K = { ...DARK };

export function setTheme(theme) {
  Object.assign(K, theme === 'light' ? LIGHT : DARK);
}

export const HOSE = {
  '1"':  { c: 150,  col: "#a78bfa", note: "Forestry line", wildland: true },
  '1½"': { c: 24,   col: "#c084fc", note: "Forestry line", wildland: true },
  '1¾"': { c: 15.5, col: "#f87171", note: "Attack line" },
  '2½"': { c: 2.0,  col: "#fb923c", note: "Blitz / backup" },
  '3"':  { c: 0.8,  col: "#fbbf24", note: "Supply / attack" },
  '4"':  { c: 0.2,  col: "#4ade80", note: "LDH supply" },
  '5"':  { c: 0.08, col: "#60a5fa", note: "LDH supply" },
};

export const APPLIANCES = {
  "Gated Wye":          { loss: 10, minGPM: 350, note: "Applied >350 GPM" },
  "Master Stream":      { loss: 25, note: "Monitor/deck gun" },
  "Aerial/Standpipe":   { loss: 25, note: "FDC or aerial" },
  "FDC Supply":         { loss: 0,  note: "Pump @ 150 psi" },
  "Siamese (2-inlet)":  { loss: 10, minGPM: 350, note: "Applied >350 GPM" },
  "Reducer/Increaser":  { loss: 5,  minGPM: 350, note: "Applied >350 GPM" },
  "Inline Gauge":       { loss: 0,  note: "No loss" },
};

export const SB_TIPS = {
  '3/8"':      { d: 0.375,  note: "Forestry", wildland: true },
  '1/2"':      { d: 0.5,    note: "Forestry", wildland: true },
  '3/4"':      { d: 0.75,   note: "Forestry", wildland: true },
  '7/8"':      { d: 0.875,  note: "Preconnect" },
  '15/16"':    { d: 0.9375, note: "" },
  '1"':        { d: 1.0,    note: "" },
  '1⅛"':      { d: 1.125,  note: "Blitz line" },
  '1³⁄₁₆"':  { d: 1.1875, note: "" },
  '1¼"':      { d: 1.25,   note: "Deck gun" },
};

export const PRECONNECTS = [
  { name: '1¾" 100ft Bumper', pdp: 110, hose: '1¾"', len: 100, nozzle: 'Automatic',     np: 100, gpm: 100, col: K.info },
  { name: '1¾" 200ft SB',     pdp: 120, hose: '1¾"', len: 200, nozzle: '7/8" SB',       np: 50,  gpm: 150, col: K.red  },
  { name: '1¾" 200ft LP',     pdp: 125, hose: '1¾"', len: 200, nozzle: 'Elkhart LP 75', np: 75,  gpm: 150, col: K.red  },
  { name: '2½" 200ft Blitz',  pdp: 80,  hose: '2½"', len: 200, nozzle: '1⅛" SB',       np: 50,  gpm: 250, col: K.gold },
  { name: 'Deck Gun',         pdp: 80,  hose: '2½"', len: 0,   nozzle: '1¼" SB',       np: 80,  gpm: 415, col: K.gold },
];

export const SHORTCUTS = [
  { diam: '2½"', name: 'Drop 10',   rule: 'First 2 digits of GPM − 10',    ex: '300 GPM → 30−10 = 20 psi/100ft' },
  { diam: '3"',  name: 'Q² method', rule: '(GPM÷100)² = FL/100ft',          ex: '450 GPM → 4.5² = 20.25 psi/100ft' },
  { diam: '4"',  name: 'Q²÷5',      rule: '(GPM÷100)²÷5 = FL/100ft',        ex: '800 GPM → 64÷5 = 12.8 psi/100ft' },
  { diam: '5"',  name: 'Divide 100', rule: 'GPM÷100 = FL/100ft',             ex: '1400 GPM → 14 psi/100ft' },
];

export const INCIDENT_TYPES = [
  { id: 'structure', label: 'Structure Fire', icon: '🏠' },
  { id: 'wildland',  label: 'Wildland Fire',  icon: '🌲' },
  { id: 'mci',       label: 'MCI / General',  icon: '🏥' },
  { id: 'medical',   label: 'Medical / EMS',  icon: '🚑' },
  { id: 'mva',       label: 'MVA',            icon: '🚗' },
  { id: 'hazmat',    label: 'HazMat',         icon: '☣️' },
  { id: 'other',     label: 'Other',          icon: '📋' },
];

export const UNIT_ROLES = [
  { id: 'attack',      label: 'Attack',       icon: '🔥' },
  { id: 'backup',      label: 'Backup',       icon: '🛡️' },
  { id: 'rit',         label: 'RIT',          icon: '🚨' },
  { id: 'water_supply',label: 'Water Supply',  icon: '💧' },
  { id: 'ventilation', label: 'Ventilation',   icon: '💨' },
  { id: 'search',      label: 'Search',        icon: '🔍' },
  { id: 'exposure',    label: 'Exposure',      icon: '🏘️' },
  { id: 'staging',     label: 'Staging',       icon: '🅿️' },
  { id: 'medical',     label: 'Medical',       icon: '🚑' },
  { id: 'command',     label: 'Command',       icon: '📋' },
];

export const BENCHMARKS = [
  { id: 'on_scene',           label: 'On Scene',               icon: '📍' },
  { id: 'water_on_fire',      label: 'Water on Fire',          icon: '💦' },
  { id: 'primary_complete',   label: 'Primary Search Complete', icon: '🔍' },
  { id: 'fire_under_ctrl',    label: 'Fire Under Control',     icon: '✅' },
  { id: 'secondary_complete', label: 'Secondary Complete',     icon: '🔎' },
  { id: 'loss_stopped',       label: 'Loss Stopped',           icon: '🛑' },
  { id: 'par_check',          label: 'PAR Check',              icon: '👥' },
  { id: 'all_clear',          label: 'All Clear',              icon: '🟢' },
];

export const CHECKLIST_TEMPLATES = {
  structure: [
    { label: '360 walk-around complete' },
    { label: 'Command established' },
    { label: 'Accountability board started' },
    { label: 'RIT assigned' },
    { label: 'Utilities secured' },
    { label: 'Exposures checked' },
    { label: 'Ventilation plan determined' },
    { label: 'Rehab established' },
    { label: 'Water supply established' },
    { label: 'Evacuation plan communicated' },
  ],
  wildland: [
    { label: 'Command established' },
    { label: 'Size-up / recon complete' },
    { label: 'Anchor point identified' },
    { label: 'Safety zones identified' },
    { label: 'Escape routes identified' },
    { label: 'Lookouts posted' },
    { label: 'Weather update obtained' },
    { label: 'Division/group assignments made' },
    { label: 'Structure triage complete' },
    { label: 'Rehab established' },
  ],
  medical: [
    { label: 'Scene safe / BSI' },
    { label: 'Patient contact made' },
    { label: 'ABCs assessed' },
    { label: 'Vitals obtained' },
    { label: 'Medical history gathered' },
    { label: 'Treatment initiated' },
    { label: 'Transport decision made' },
    { label: 'Hospital notified' },
  ],
  mva: [
    { label: 'Scene safe / traffic control' },
    { label: 'Hazards mitigated (fluids, batteries)' },
    { label: 'Vehicle stabilized' },
    { label: 'Patient access established' },
    { label: 'Patient assessment complete' },
    { label: 'Extrication plan determined' },
    { label: 'EMS staged / ready' },
    { label: 'Patient packaged and transported' },
  ],
  hazmat: [
    { label: 'Isolate / deny entry' },
    { label: 'Product identified (placard/SDS)' },
    { label: 'Hot / warm / cold zones established' },
    { label: 'Decon corridor set up' },
    { label: 'Entry team briefed' },
    { label: 'Backup team in place' },
    { label: 'Medical monitoring active' },
    { label: 'Containment / mitigation started' },
  ],
  mci: [
    { label: 'MCI declared and level determined' },
    { label: 'Triage officer assigned' },
    { label: 'Treatment officer assigned' },
    { label: 'Transport officer assigned' },
    { label: 'Staging area established' },
    { label: 'Treatment areas set up (Red/Yellow/Green)' },
    { label: 'Hospital notifications made' },
    { label: 'Additional resources requested' },
    { label: 'Mutual aid contacted if needed' },
    { label: 'Patient tracking initiated' },
  ],
  other: [
    { label: 'Command established' },
    { label: 'Scene size-up complete' },
    { label: 'Resources requested' },
    { label: 'Action plan communicated' },
    { label: 'Accountability maintained' },
    { label: 'Rehab established' },
  ],
};

export const STRATEGIES = [
  { id: 'aggressive_interior', label: 'Aggressive Interior', col: '#c75b54' },
  { id: 'modified_exterior',   label: 'Modified Exterior',   col: '#d6a64e' },
  { id: 'defensive_exterior',  label: 'Defensive Exterior',  col: '#6aa5e0' },
];

export const WILDLAND_STRATEGIES = [
  { id: 'direct_attack',   label: 'Direct Attack',   col: '#c75b54' },
  { id: 'parallel_attack', label: 'Parallel Attack',  col: '#d6a64e' },
  { id: 'indirect_attack', label: 'Indirect Attack',  col: '#6aa5e0' },
];

export const WILDLAND_BENCHMARKS = [
  { id: 'on_scene',        label: 'On Scene',                    icon: '📍' },
  { id: 'size_up',         label: 'Size-Up Complete',            icon: '🔍' },
  { id: 'anchor_point',    label: 'Anchor Point Set',            icon: '⚓' },
  { id: 'line_started',    label: 'Line Construction Started',   icon: '🔥' },
  { id: 'containment',     label: 'Containment Achieved',        icon: '✅' },
  { id: 'mop_up',          label: 'Mop-Up Started',              icon: '🧹' },
  { id: 'par_check',       label: 'PAR Check',                   icon: '👥' },
  { id: 'controlled',      label: 'Fire Controlled',             icon: '🟢' },
];

export const MCI_BENCHMARKS = [
  { id: 'on_scene',               label: 'On Scene',                    icon: '📍' },
  { id: 'mci_declared',           label: 'MCI Declared',                icon: '🚨' },
  { id: 'triage_started',         label: 'Triage Started',              icon: '🏷️' },
  { id: 'treatment_set',          label: 'Treatment Area Set',          icon: '🏥' },
  { id: 'transport_started',      label: 'Transport Started',           icon: '🚑' },
  { id: 'triage_complete',        label: 'Triage Complete',             icon: '✅' },
  { id: 'par_check',              label: 'PAR Check',                   icon: '👥' },
  { id: 'all_patients_transported', label: 'All Patients Transported',  icon: '🟢' },
];

export const ASSIGNMENT_SLOTS = [
  { id: 'search',       label: 'Search' },
  { id: 'fire_attack',  label: 'Fire Attack' },
  { id: 'ventilation',  label: 'Vent' },
  { id: 'rit',          label: 'R.I.T. / F.A.S.T.' },
  { id: 'water_supply', label: 'Water Supply' },
  { id: 'backup',       label: 'Backup' },
  { id: 'medical',      label: 'Medical' },
  { id: 'exposure',     label: 'Exposure' },
  { id: 'staging',      label: 'Staging' },
];

export const WILDLAND_ASSIGNMENT_SLOTS = [
  { id: 'division_a',            label: 'Division A' },
  { id: 'division_b',            label: 'Division B' },
  { id: 'division_c',            label: 'Division C' },
  { id: 'structure_protection',  label: 'Structure Protection' },
  { id: 'dozer',                 label: 'Dozer / Heavy Equip' },
  { id: 'water_supply',          label: 'Water Supply' },
  { id: 'lookout',               label: 'Lookout' },
  { id: 'medical',               label: 'Medical' },
  { id: 'staging',               label: 'Staging' },
];

export const MCI_ASSIGNMENT_SLOTS = [
  { id: 'triage',           label: 'Triage' },
  { id: 'treatment_red',    label: 'Treatment (Red)' },
  { id: 'treatment_yellow', label: 'Treatment (Yellow)' },
  { id: 'treatment_green',  label: 'Treatment (Green)' },
  { id: 'transport',        label: 'Transport' },
  { id: 'staging',          label: 'Staging' },
  { id: 'ems_branch',       label: 'EMS Branch' },
  { id: 'rehab',            label: 'Rehab' },
  { id: 'perimeter',        label: 'Perimeter / Security' },
];

export const APPARATUS_DEFAULT = [
  // Engines — Red
  { unit: 'Engine 21',   type: 'engine',  tagColor: '#cc1f26' },
  { unit: 'Engine 27',   type: 'engine',  tagColor: '#cc1f26' },
  { unit: 'Engine 46',   type: 'engine',  tagColor: '#cc1f26' },
  { unit: 'Engine 51',   type: 'engine',  tagColor: '#cc1f26', name: '2020 Rosenbauer', pump: 1500, tank: 1000 },
  { unit: 'Engine 53',   type: 'engine',  tagColor: '#cc1f26' },
  { unit: 'Engine 54',   type: 'engine',  tagColor: '#cc1f26', name: '2020 Rosenbauer', pump: 1500, tank: 1000 },
  { unit: 'Engine 57',   type: 'engine',  tagColor: '#cc1f26', name: '2020 Rosenbauer', pump: 1500, tank: 1000 },
  { unit: 'Engine 58',   type: 'engine',  tagColor: '#cc1f26', name: '2020 Rosenbauer', pump: 1500, tank: 1000 },
  { unit: 'Engine 511',  type: 'engine',  tagColor: '#cc1f26' },
  { unit: 'Engine 16-1', type: 'engine',  tagColor: '#cc1f26' },
  { unit: 'Engine 582',  type: 'engine',  tagColor: '#cc1f26' },
  // Brush — Green
  { unit: 'Brush 21',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 46',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 51',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 52',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 53',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 54',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 56',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 57',  type: 'brush',  tagColor: '#22c55e' },
  { unit: 'Brush 58',  type: 'brush',  tagColor: '#22c55e' },
  // Tenders — Blue
  { unit: 'Tender 21',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 46',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 51',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 52',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 53',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 56',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 57',  type: 'tender',  tagColor: '#3b82f6' },
  { unit: 'Tender 511', type: 'tender',  tagColor: '#3b82f6' },
  // Chiefs — Orange
  { unit: 'Chief 500', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 501', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 502', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 503', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 504', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 505', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 506', type: 'chief', tagColor: '#f97316' },
  { unit: 'Chief 507', type: 'chief', tagColor: '#f97316' },
  // Battalion — Gold
  { unit: 'Batt 58', type: 'battalion', tagColor: '#f59e0b' },
  // TAC — Teal
  { unit: 'TAC 53', type: 'tac', tagColor: '#14b8a6' },
  // Medics — Purple
  { unit: 'Medic 21',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 51',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 52',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 53',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 54',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 57',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 58',  type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 511', type: 'medic', tagColor: '#8b5cf6' },
  { unit: 'Medic 582', type: 'medic', tagColor: '#8b5cf6' },
  // Aid — Light gray
  { unit: 'Aid 51',  type: 'aid', tagColor: '#a3a3a3' },
  { unit: 'Aid 52',  type: 'aid', tagColor: '#a3a3a3' },
  { unit: 'Aid 58',  type: 'aid', tagColor: '#a3a3a3' },
  { unit: 'Aid 511', type: 'aid', tagColor: '#a3a3a3' },
  // Marshal — Brown
  { unit: 'Marshal 51', type: 'marshal', tagColor: '#a16207' },
  { unit: 'Marshal 52', type: 'marshal', tagColor: '#a16207' },
  // Truck / Ladder — Red
  { unit: 'Truck 52',  type: 'truck',  tagColor: '#cc1f26' },
  { unit: 'Ladder 58', type: 'ladder', tagColor: '#cc1f26' },
  // Special — White/gray
  { unit: 'Air Unit', type: 'special', tagColor: '#d4d4d4' },
  { unit: 'Safety',   type: 'special', tagColor: '#d4d4d4' },
];

export const OPS_ALL = [
  { id: 'layout',    label: 'Hose Layout',  icon: '🔴', desc: 'Calculate pump discharge pressure' },
  { id: 'pc',        label: 'Preconnects',  icon: '📋', desc: 'Preconnect PDP reference' },
  { id: 'supply',    label: 'Water Supply', icon: '💧', desc: 'Hydrant & supply line calcs' },
  { id: 'relay',     label: 'Relay Pump',   icon: '🔄', desc: 'Relay pumping operations' },
  { id: 'custom',    label: 'Quick Calc',   icon: '🧮', desc: 'Custom friction loss calculator' },
  { id: 'flref',     label: 'FL Reference', icon: '📐', desc: 'Friction loss tables & rules' },
  { id: 'gpm',       label: 'GPM / NR',     icon: '💧', desc: 'Flow rate & nozzle reaction' },
  { id: 'multiline', label: 'Multi-Line',   icon: '🔀', desc: 'Multiple simultaneous lines' },
  { id: 'draft',     label: 'Drafting',     icon: '🌊', desc: 'Draft / lift calculations' },
  { id: 'apparatus', label: 'Apparatus',    icon: '🚒', desc: 'Apparatus specs & water tank' },
  { id: 'resources', label: 'Resources',    icon: '📚', desc: 'Study guides & documents' },
];

export const CMD_ALL = [
  { id: 'incident',  label: 'Incident Cmd', icon: '🚨', desc: 'Incident command timekeeping' },
  { id: 'history',   label: 'History',      icon: '📜', desc: 'Search past incidents' },
  { id: 'apparatus', label: 'Apparatus',    icon: '🚒', desc: 'Apparatus specs & water tank' },
  { id: 'resources', label: 'Resources',    icon: '📚', desc: 'Study guides & documents' },
];

export const TRAIN_ALL = [
  { id: 'training-hub', label: 'Training Hub', icon: '📋', desc: 'Daily training, calendar & flyers' },
  { id: 'sim',        label: 'Pump Simulator', icon: '⚙️', desc: '3D pump you can cut open (Beta)' },
  { id: 'evolutions', label: 'Evolutions',   icon: '🔥', desc: 'Create & run fire training evolutions' },
  { id: 'quiz',       label: 'Quiz',         icon: '🧠', desc: 'Test your hydraulics knowledge' },
  { id: 'flashcards', label: 'Flashcards',   icon: '🃏', desc: 'Drill coefficients & formulas' },
  { id: 'leaderboard',label: 'Leaderboard',  icon: '🏆', desc: 'Quiz rankings & scores' },
  { id: 'podcast',    label: 'Podcast',      icon: '🎙️', desc: 'Department podcast episodes' },
  { id: 'resources',  label: 'Resources',    icon: '📚', desc: 'Study guides & documents' },
];

export const EMS_ALL = [
  { id: 'cprtimer',   label: 'CPR Timer',    icon: '💓', desc: 'CPR timer with metronome' },
  { id: 'drugcalc',   label: 'Drug Calc',    icon: '💊', desc: 'Weight-based drug dose calculator' },
  { id: 'assessment', label: 'Assessments',  icon: '📝', desc: 'GCS, stroke scales, LAMS, APGAR' },
  { id: 'burncalc',   label: 'Burn Calc',    icon: '🔥', desc: 'Rule of 9s burn calculator' },
  { id: 'pediatric',  label: 'Pediatric',    icon: '👶', desc: 'Broselow pediatric reference' },
  { id: 'ecg',        label: 'ECG Practice',  icon: '❤️', desc: 'ECG rhythm identification' },
];

export function inputStyle() {
  return {
    background: K.bg3,
    border: `1px solid ${K.bdr}`,
    borderRadius: 8,
    color: K.t0,
    fontFamily: "'DM Mono', monospace",
    fontSize: 16,
    padding: '12px 14px',
    width: '100%',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
  };
}
